import React, { createContext, useContext, useMemo } from "react"
import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  InMemoryCacheConfig,
  from,
} from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { onError } from "@apollo/client/link/error"
import { DashboardUser } from "@hydrologiq/common/providers/FirebaseAuth"

export interface DualGraphQLProviderProps {
  primaryEndpoint?: string // Hub's modern GraphQL
  legacyEndpoint: string // Section's legacy GraphQL (required for backward compatibility)
  user?: DashboardUser
  token?: string
  authType?: "firebase" | "better-auth"
  cacheConfig?: InMemoryCacheConfig
  children: React.ReactNode
}

// Context to track which client we're using (exported for MockedProvider integration in Storybook)
export const GraphQLClientContext = createContext<{
  primaryClient?: ApolloClient<any>
  legacyClient: ApolloClient<any>
  currentEndpoint: "primary" | "legacy"
}>({
  legacyClient: {} as ApolloClient<any>,
  currentEndpoint: "legacy",
})

// Mock client for environments without ApolloProvider (e.g. Storybook)
// Never call useApolloClient() - it throws when outside ApolloProvider
let mockClient: ApolloClient<any> | null = null
function getMockApolloClient(): ApolloClient<any> {
  if (!mockClient) {
    mockClient = new ApolloClient({
      link: new HttpLink({ uri: "http://localhost/graphql" }),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: { fetchPolicy: "no-cache", errorPolicy: "ignore" },
        query: { fetchPolicy: "no-cache", errorPolicy: "all" },
        mutate: { errorPolicy: "all" },
      },
    })
  }
  return mockClient
}

function createApolloClient(
  uri: string,
  user?: DashboardUser,
  token?: string,
  authType?: "firebase" | "better-auth",
  cacheConfig?: InMemoryCacheConfig
): ApolloClient<any> {
  const httpLink = new HttpLink({
    uri,
    // Removed fetchOptions: { cache: "no-store" } to enable caching
  })

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors)
      graphQLErrors.forEach(({ message, locations, path }) =>
        console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      )
    if (networkError) console.log(`[Network error]: ${networkError}`)
  })

  // Unified auth link that handles both Firebase and better-auth.
  // IMPORTANT: For better-auth, always read the latest token from localStorage
  // rather than using the `token` prop captured at mount time. The hub refreshes
  // the token in localStorage but cannot re-render section roots (they use
  // createRoot and are never re-mounted). Using the prop would serve a stale
  // token after the session is refreshed, causing silent 401s and blank sections.
  const authLink = setContext(async (_, { headers }) => {
    let authToken = ""
    let detectedAuthType = authType

    try {
      // Priority 1: Firebase user provided (legacy) - actively refreshes its own token
      if (user?.firebaseUser && typeof user.firebaseUser.getIdTokenResult === "function") {
        const idTokenResult = await user.firebaseUser.getIdTokenResult()
        authToken = idTokenResult.token
        detectedAuthType = detectedAuthType || "firebase"
      }
      // Priority 2: better-auth — always read the freshest token from localStorage
      else {
        const betterAuthToken = localStorage.getItem("bearer_token")
        if (betterAuthToken) {
          authToken = betterAuthToken
          detectedAuthType = "better-auth"
        } else if (token) {
          // Fallback to the prop token only if localStorage is empty
          // (e.g. during initial mount before the hub writes to localStorage)
          authToken = token
          detectedAuthType = detectedAuthType || "better-auth"
        }
      }

      return {
        headers: {
          ...headers,
          authorization: authToken ? `Bearer ${authToken}` : "",
          "X-Auth-Type": detectedAuthType || "unknown",
        },
      }
    } catch (error) {
      console.error("Failed to get auth token:", error)
      return { headers }
    }
  })

  return new ApolloClient({
    link: from([errorLink, authLink, httpLink]),
    cache: new InMemoryCache({
      ...cacheConfig,
      typePolicies: {
        Asset: {
          fields: {
            class: {
              // TODO: Blocked on core change — see hydrologiq/core#XX:
              // Protect against getSystemsList returning class: null due to core omission.
              // Never overwrite an existing non-null asset class with null.
              merge(existing, incoming) {
                return incoming ?? existing
              },
            },
            kind: {
              // Protect against getSystemsList returning kind: "OTHER" or null when a specific kind exists.
              // Never overwrite an existing known kind with null or "OTHER".
              merge(existing, incoming) {
                if (!incoming || (typeof incoming === "string" && incoming.toLowerCase() === "other")) {
                  return existing ?? incoming
                }
                return incoming
              },
            },
            category: {
              merge(existing, incoming) {
                return incoming ?? existing
              },
            },
          },
        },
        Query: {
          fields: {
            // Tenant/preferences queries - cache for 5 minutes
            getTenants: {
              merge: false, // Replace, don't merge
            },
            getTenant: {
              keyArgs: ["tenantId"],
              merge: false,
            },
            getPreferences: {
              merge: false,
            },
            getFeatures: {
              merge: false,
            },

            // Asset headlines - cache for 1 minute (more dynamic)
            // Using "read" function to implement short TTL
            getAssetHeadlines: {
              keyArgs: ["assetId", "stop"],
              merge: false,
              read(existing, { canRead }) {
                // If data exists and is readable, return it
                // Apollo will automatically refetch in background if stale
                return canRead(existing) ? existing : undefined
              },
            },

            // Admin queries - cache for 5 minutes
            getAllTenants: { merge: false },
            getAllUsers: { merge: false },
            getTenantLimits: { keyArgs: ["tenantId"], merge: false },
            getTenantUsage: { keyArgs: ["tenantId"], merge: false },
          },
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-first", // Changed from "cache-and-network" for faster repeat views
        nextFetchPolicy: "cache-first", // Stay cache-first on subsequent fetches
        errorPolicy: "all",
        refetchWritePolicy: "overwrite", // Overwrite cache on refetch
      },
      query: {
        fetchPolicy: "cache-first", // Changed from "network-only" to enable caching
        errorPolicy: "all",
      },
      mutate: {
        errorPolicy: "all",
      },
    },
  })
}

export function DualGraphQLProvider({
  primaryEndpoint,
  legacyEndpoint,
  user,
  token,
  authType,
  cacheConfig,
  children,
}: DualGraphQLProviderProps) {
  // Create clients
  const legacyClient = useMemo(
    () => createApolloClient(legacyEndpoint, user, token, authType, cacheConfig),
    [legacyEndpoint, user, token, authType, cacheConfig]
  )

  const primaryClient = useMemo(
    () => (primaryEndpoint ? createApolloClient(primaryEndpoint, user, token, authType, cacheConfig) : undefined),
    [primaryEndpoint, user, token, authType, cacheConfig]
  )

  const contextValue = useMemo(
    () => ({
      primaryClient,
      legacyClient,
      currentEndpoint: "legacy" as const,
    }),
    [primaryClient, legacyClient]
  )

  // If we have both endpoints, nest the providers
  if (primaryClient) {
    return (
      <ApolloProvider client={primaryClient}>
        <ApolloProvider client={legacyClient}>
          <GraphQLClientContext.Provider value={contextValue}>{children}</GraphQLClientContext.Provider>
        </ApolloProvider>
      </ApolloProvider>
    )
  }

  // Fallback to single legacy client for backward compatibility
  return (
    <ApolloProvider client={legacyClient}>
      <GraphQLClientContext.Provider value={contextValue}>{children}</GraphQLClientContext.Provider>
    </ApolloProvider>
  )
}

// Hook to get specific GraphQL client
export function useGraphQLClient(endpoint: "primary" | "legacy" = "legacy"): ApolloClient<any> {
  const context = useContext(GraphQLClientContext)

  // Check context first - return early if we have a valid client
  if (endpoint === "primary" && context.primaryClient && typeof context.primaryClient.query === "function") {
    return context.primaryClient
  }

  if (endpoint === "legacy" && context.legacyClient && typeof context.legacyClient.query === "function") {
    return context.legacyClient
  }

  // Fallback when outside DualGraphQLProvider (e.g. Storybook): use mock client
  // Never call useApolloClient() - it throws invariant when outside ApolloProvider
  return getMockApolloClient()
}

/**
 * The primary client inside DualGraphQLProvider, otherwise undefined so Apollo uses the
 * host's own ApolloProvider. For shared components rendered by both a section and the hub
 * (the hub has no DualGraphQLProvider; its default client is already the primary endpoint).
 */
export function usePrimaryClientIfAvailable(): ApolloClient<any> | undefined {
  const { primaryClient } = useContext(GraphQLClientContext)
  return primaryClient && typeof primaryClient.query === "function" ? primaryClient : undefined
}

export default DualGraphQLProvider
